// src/components/OperatorManagement/OperatorManagement.js
import React, { useEffect, useState } from 'react';
import { Spinner, Alert, Form, Button, Container, Table, Modal } from 'react-bootstrap';
import 'bootstrap/dist/css/bootstrap.min.css';
import apiService from "../../API/ApiService.js";

const OperatorManagement = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [userId, setUserId] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);

  const [newOperator, setNewOperator] = useState({ uniqueIdentifier: '', name: '', idRole: '' });
  const [selectedOperator, setSelectedOperator] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const username = localStorage.getItem('username');

  // ------------------- FETCH USER ID -------------------------
  useEffect(() => {
    const fetchUserId = async () => {
      if (!username) { setError('User not logged in'); setLoading(false); return; }
      try {
        const response = await apiService.get('/getUserIdByEmail', { params: { email: username } });
        setUserId(response.data.userId);
      } catch (err) {
        setError('Failed to fetch user information');
        console.error(err);
      } finally { setLoading(false); }
    };
    fetchUserId();
  }, [username]);

  // ------------------- FETCH OPERATORS -------------------------
  const fetchOperators = async () => {
    if (!userId) return;
    try {
      // D'abord, récupérer tous les opérateurs
      const allOperatorsResponse = await apiService.get('/getAllOperator');
      console.log('All operators:', allOperatorsResponse.data);
      
      // Ensuite, récupérer les opérateurs avec les rôles pour cet utilisateur
      const response = await apiService.get(`/getOperatorsByUserId/${userId}`);
      console.log('Operators with roles:', response.data);
      
      // Fusionner les données des deux APIs
      const ops = response.data.map(opWithRole => {
        // Trouver l'opérateur correspondant dans la liste complète
        const fullOperator = allOperatorsResponse.data.find(
          op => op.id === opWithRole.id || op.operatorId === opWithRole.id
        );
        
        // Déterminer le rôle
        let role;
        let idRoleValue;
        
        if (opWithRole.role) {
          // Utiliser le rôle de l'API getOperatorsByUserId
          role = opWithRole.role;
          // Mapper le rôle texte vers idRole
          if (role.toLowerCase() === 'quality') {
            idRoleValue = 1;
          } else if (role.toLowerCase() === 'operator') {
            idRoleValue = 15;
          } else {
            // Si c'est un autre rôle, essayer de le convertir en nombre
            idRoleValue = Number(fullOperator?.idRole) || 15;
          }
        } else {
          // Si pas de rôle dans getOperatorsByUserId, utiliser idRole de getAllOperator
          idRoleValue = Number(fullOperator?.idRole) || 15;
          role = idRoleValue === 1 ? 'Quality' : 'Operator';
        }
        
        return {
          id: opWithRole.id || fullOperator?.id,
          uniqueIdentifier: opWithRole.uniqueIdentifier || fullOperator?.uniqueIdentifier || '',
          name: opWithRole.name || fullOperator?.name || '',
          user_id: opWithRole.user_id || fullOperator?.user_id,
          idRole: idRoleValue,
          roleLabel: role // Stocker le label du rôle pour l'affichage
        };
      });
      
      console.log('Operators after processing:', ops);
      setOperators(ops);
    } catch (err) {
      setError('Failed to load operators');
      console.error('fetchOperators error:', err);
    }
  };

  useEffect(() => { 
    if (userId) {
      fetchOperators(); 
    }
  }, [userId]);

  // ------------------- HANDLE INPUT CHANGE -------------------------
  const handleChange = (e) => { 
    const { name, value } = e.target; 
    setNewOperator(prev => ({ ...prev, [name]: value })); 
  };

  // ------------------- ADD OPERATOR -------------------------
  const handleAddOperator = async (e) => {
    e.preventDefault();
    if (!newOperator.uniqueIdentifier || !newOperator.name || !newOperator.idRole) {
      setError('All fields are required'); return; 
    }
    const role = Number(newOperator.idRole);
    try {
      const response = await apiService.post('/operators', {
        uniqueIdentifier: newOperator.uniqueIdentifier.trim(),
        name: newOperator.name.trim(),
        idRole: role,
        user_id: userId,
      });
      const addedOperator = response.data.newOperator || response.data;
      const newId = addedOperator.id || addedOperator.operatorId;
      
      // Après l'ajout, rafraîchir la liste
      await fetchOperators();
      
      setMessage('Operator added successfully!');
      setNewOperator({ uniqueIdentifier: '', name: '', idRole: '' });
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add operator'); 
      setMessage('');
    }
  };

  // ------------------- OPEN EDIT MODAL -------------------------
  const handleEditOperator = (operator) => {
    console.log('Selected operator for edit:', operator);
    setSelectedOperator({
      ...operator,
      id: operator.id,
      uniqueIdentifier: operator.uniqueIdentifier || '',
      name: operator.name || '',
      idRole: operator.idRole !== undefined ? operator.idRole.toString() : '15',
    });
    setShowModal(true); 
    setError('');
  };

  // ------------------- UPDATE OPERATOR -------------------------
  const handleUpdateOperator = async (e) => {
    e.preventDefault();
    if (!selectedOperator || !selectedOperator.uniqueIdentifier || !selectedOperator.name || !selectedOperator.idRole) {
      setError('All fields are required'); return; 
    }

    setIsUpdating(true);
    const role = Number(selectedOperator.idRole);

    const payload = {
      uniqueIdentifier: selectedOperator.uniqueIdentifier.trim(),
      name: selectedOperator.name.trim(),
      role,
    };

    console.log('Payload before sending to backend:', payload, 'Operator ID:', selectedOperator.id);

    try {
      const response = await apiService.put(`/operators/${selectedOperator.id}`, payload);
      console.log('Backend response:', response.data);
      await fetchOperators(); // refresh list
      setMessage(`Operator updated (${role === 1 ? 'Quality' : 'Operator'})!`);
      setSelectedOperator(null);
      setShowModal(false);
      setError('');
    } catch (err) {
      console.error('Operator update error', err);
      setError(err.response?.data?.error || 'Update failed. Check server.');
    } finally {
      setIsUpdating(false);
    }
  };

  // ------------------- CLOSE MODAL -------------------------
  const handleCloseModal = () => { 
    if (!isUpdating) {
      setShowModal(false); 
      setSelectedOperator(null); 
      setError('');
    }
  };

  // ------------------- UTILITIES -------------------------
  const getRoleLabel = (op) => {
    if (op.roleLabel) return op.roleLabel;
    return op.idRole === 1 ? 'Quality' : 'Operator';
  };
  
  const getBadgeClass = (op) => {
    const role = getRoleLabel(op);
    return role.toLowerCase() === 'quality' ? 'bg-primary' : 'bg-secondary';
  };

  if (loading) return (
    <div className="d-flex justify-content-center align-items-center vh-100">
      <Spinner animation="border" role="status">
        <span className="visually-hidden">Loading...</span>
      </Spinner>
    </div>
  );

  return (
    <Container className="py-5">
      {message && <Alert variant="success" onClose={() => setMessage('')} dismissible>{message}</Alert>}
      {error && <Alert variant="danger" onClose={() => setError('')} dismissible>{error}</Alert>}

      <h3 className="text-center mb-5 text-primary fw-bold">Operator Management</h3>

      {/* Add Operator Form */}
      <div className="card shadow-sm mb-5 p-4">
        <h4 className="mb-4">Add Operator</h4>
        <Form onSubmit={handleAddOperator}>
          <div className="row g-3">
            <div className="col-md-4">
              <Form.Label>Unique Identifier</Form.Label>
              <Form.Control type="text" name="uniqueIdentifier" value={newOperator.uniqueIdentifier} onChange={handleChange} required placeholder="Ex: OP-2025-001" />
            </div>
            <div className="col-md-4">
              <Form.Label>Name</Form.Label>
              <Form.Control type="text" name="name" value={newOperator.name} onChange={handleChange} required placeholder="John Doe" />
            </div>
            <div className="col-md-3">
              <Form.Label>Role</Form.Label>
              <Form.Select name="idRole" value={newOperator.idRole} onChange={handleChange} required>
                <option value="">Select...</option>
                <option value="1">Quality</option>
                <option value="15">Operator</option>
              </Form.Select>
            </div>
            <div className="col-md-1 d-flex align-items-end">
              <Button variant="success" type="submit" className="w-100">Add</Button>
            </div>
          </div>
        </Form>
      </div>

      {/* Operator List */}
      <h4 className="mb-3">Operator List</h4>
      {operators.length === 0 ? <Alert variant="info">No operators found.</Alert> : (
        <Table striped bordered hover responsive className="shadow-sm">
          <thead className="table-dark">
            <tr>
              <th>ID</th><th>Unique Identifier</th><th>Name</th><th>Role</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {operators.map(op => (
              <tr key={op.id}>
                <td>{op.id}</td>
                <td><strong>{op.uniqueIdentifier}</strong></td>
                <td>{op.name || '—'}</td>
                <td>
                  <span className={`badge ${getBadgeClass(op)}`}>
                    {getRoleLabel(op)}
                  </span>
                </td>
                <td>
                  <Button size="sm" variant="outline-warning" onClick={() => handleEditOperator(op)} disabled={isUpdating}>Edit</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      )}

      {/* Edit Modal */}
      <Modal show={showModal} onHide={handleCloseModal} centered>
        <Modal.Header closeButton={!isUpdating}>
          <Modal.Title>Edit Operator {isUpdating && <Spinner animation="border" size="sm" className="ms-2" />}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedOperator && (
            <Form onSubmit={handleUpdateOperator}>
              <Form.Group className="mb-3">
                <Form.Label>Unique Identifier</Form.Label>
                <Form.Control type="text" value={selectedOperator.uniqueIdentifier} onChange={(e) => setSelectedOperator({ ...selectedOperator, uniqueIdentifier: e.target.value })} required disabled={isUpdating} />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Name</Form.Label>
                <Form.Control type="text" value={selectedOperator.name} onChange={(e) => setSelectedOperator({ ...selectedOperator, name: e.target.value })} required disabled={isUpdating} />
              </Form.Group>
              <Form.Group className="mb-4">
                <Form.Label>Role</Form.Label>
                <Form.Select value={selectedOperator.idRole} onChange={(e) => setSelectedOperator({ ...selectedOperator, idRole: e.target.value })} required disabled={isUpdating}>
                  <option value="">Select...</option>
                  <option value="1">Quality</option>
                  <option value="15">Operator</option>
                </Form.Select>
              </Form.Group>
              <div className="d-flex gap-2 justify-content-end">
                <Button variant="secondary" onClick={handleCloseModal} disabled={isUpdating}>Cancel</Button>
                <Button variant="success" type="submit" disabled={isUpdating}>{isUpdating ? 'Updating...' : 'Save'}</Button>
              </div>
            </Form>
          )}
        </Modal.Body>
      </Modal>
    </Container>
  );
};

export default OperatorManagement;